/**
 * `growth/caps` — **local-first** frequency-capping state.
 *
 * It lives on the device under namespaced `ads.cap.<placement>.<day>` keys →
 * it works **offline** and **for guests** (guest-first: never ask for an account
 * for capping to be real). If an account exists, it can sync as a preference,
 * but enforcement is ALWAYS local.
 *
 * The store is environment-agnostic: it receives a minimal `AdCapsStorage`
 * (`localStorage` satisfies it as-is, and a `Map` serves tests and SSR).
 */

import type { AdDayKey, AdReward, CapsState } from "./types";

/** Subset of `Storage` the store needs (localStorage satisfies it). */
export interface AdCapsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Canonical prefix for capping keys (§4 of the strategy). */
export const AD_CAPS_KEY_PREFIX = "ads.cap";

/** Key for a specific day: `ads.cap.<scope>.<YYYY-MM-DD>`. */
export function capsKey(scope: string, day: AdDayKey): string {
  return `${AD_CAPS_KEY_PREFIX}.${scope}.${day}`;
}

/** Local `YYYY-MM-DD` day for the given instant (device time zone). */
export function adDayKey(at: number = Date.now()): AdDayKey {
  const d = new Date(at);
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/** Initial state for a clean day. */
export function emptyCaps(day: AdDayKey = adDayKey()): CapsState {
  return {
    day,
    interstitialsToday: 0,
    interstitialsThisSession: 0,
    rewardsToday: {},
    takenToday: {},
    offersToday: {},
    ignoredDays: {},
    nativesOnScreen: 0,
    bannersVisible: 0,
  };
}

/** Part of the state that survives a day change (backoff spans multiple days). */
function carryOver(previous: CapsState | undefined, day: AdDayKey): CapsState {
  const fresh = emptyCaps(day);
  if (!previous) {
    return fresh;
  }
  // Days with an offer shown but no reward accumulate: that is precisely the
  // backoff signal ("ignore 3 days in a row ⇒ show fewer offers").
  const ignoredDays: Record<string, number> = { ...previous.ignoredDays };
  for (const [placement, offers] of Object.entries(previous.offersToday)) {
    if (offers <= 0) {
      continue;
    }
    ignoredDays[placement] =
      (previous.takenToday[placement] ?? 0) > 0 ? 0 : (ignoredDays[placement] ?? 0) + 1;
  }
  return { ...fresh, ignoredDays };
}

function isCapsState(value: unknown): value is CapsState {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const v = value as Partial<CapsState>;
  return typeof v.day === "string" && typeof v.interstitialsToday === "number";
}

/**
 * Local-first capping store.
 *
 * - `load()` normalizes the day: if the saved state is from YESTERDAY, it starts
 *   a new one (carrying over only the backoff) — that is why `adGate` can read
 *   daily counters without knowing anything about time zones.
 * - **Session and screen counters** (`interstitialsThisSession`,
 *   `nativesOnScreen`, `bannersVisible`) are NOT persisted: they belong to the
 *   live process.
 */
export class AdCapsStore {
  private state: CapsState;
  /** Rewarded offers already counted in THIS session (remount dedupe). */
  private readonly sessionOffers = new Set<string>();

  constructor(
    private readonly storage: AdCapsStorage,
    /** Device/profile namespace (including guest). */
    private readonly scope = "device",
    now: number = Date.now(),
  ) {
    this.state = this.load(now);
  }

  private key(day: AdDayKey): string {
    return capsKey(this.scope, day);
  }

  private load(now: number): CapsState {
    const day = adDayKey(now);
    let stored: CapsState | undefined;
    try {
      const raw = this.storage.getItem(this.key(day));
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        if (isCapsState(parsed)) {
          stored = parsed;
        }
      }
    } catch {
      // Full storage, corrupt JSON, or private mode: capping degrades to a
      // "clean day", never bringing down the app over an ad counter.
      stored = undefined;
    }
    if (stored && stored.day === day) {
      // New session ⇒ session/screen counters start at zero.
      return { ...stored, interstitialsThisSession: 0, nativesOnScreen: 0, bannersVisible: 0 };
    }
    return carryOver(stored, day);
  }

  private persist(): void {
    try {
      this.storage.setItem(this.key(this.state.day), JSON.stringify(this.state));
    } catch {
      // Likewise: in-memory enforcement remains alive even if it cannot be saved.
    }
  }

  /** Immutable snapshot for the (pure) `adGate`. */
  snapshot(now: number = Date.now()): CapsState {
    const day = adDayKey(now);
    if (day !== this.state.day) {
      this.state = carryOver(this.state, day);
      this.persist();
    }
    return { ...this.state };
  }

  /** Records an interstitial impression (the only interruptive one). */
  recordInterstitial(now: number = Date.now()): void {
    const s = this.snapshot(now);
    this.state = {
      ...s,
      interstitialsToday: s.interstitialsToday + 1,
      interstitialsThisSession: s.interstitialsThisSession + 1,
      lastInterstitialAt: now,
    };
    this.persist();
  }

  /**
   * Records that a rewarded offer was SHOWN (even if it was not clicked).
   *
   * **Idempotent per session**: an offer the user sees once is ONE impression,
   * even if the component remounts while navigating between screens. Without
   * this guard, two remounts exhausted the daily cap (2) before the user could
   * press the button — and the offer disappeared on its own.
   */
  recordOffer(placementId: string, now: number = Date.now()): void {
    if (this.sessionOffers.has(placementId)) {
      return;
    }
    this.sessionOffers.add(placementId);
    const s = this.snapshot(now);
    this.state = {
      ...s,
      offersToday: { ...s.offersToday, [placementId]: (s.offersToday[placementId] ?? 0) + 1 },
    };
    this.persist();
  }

  /** Records a GRANTED reward (anti-farming by type) and clears backoff. */
  recordReward(reward: AdReward, placementId?: string, now: number = Date.now()): void {
    const s = this.snapshot(now);
    const ignoredDays = { ...s.ignoredDays };
    const takenToday = { ...s.takenToday };
    if (placementId) {
      ignoredDays[placementId] = 0;
      takenToday[placementId] = (takenToday[placementId] ?? 0) + 1;
    }
    this.state = {
      ...s,
      rewardsToday: {
        ...s.rewardsToday,
        // The daily cap counts REWARDS, not units: two ads that each give
        // "+1 freeze" are 2 redemptions, not 2 units of one redemption.
        [reward.type]: (s.rewardsToday[reward.type] ?? 0) + 1,
      },
      takenToday,
      ignoredDays,
    };
    this.persist();
  }

  /** SCREEN counters (not persisted): when mounting/unmounting a slot. */
  setScreenCounts(counts: { natives?: number; banners?: number }): void {
    this.state = {
      ...this.state,
      nativesOnScreen: counts.natives ?? this.state.nativesOnScreen,
      bannersVisible: counts.banners ?? this.state.bannersVisible,
    };
  }

  /** After a premium purchase: 0 ads immediately ⇒ local capping is forgotten. */
  reset(now: number = Date.now()): void {
    this.sessionOffers.clear();
    this.state = emptyCaps(adDayKey(now));
    try {
      this.storage.removeItem(this.key(this.state.day));
    } catch {
      // no consequences: the in-memory state is already clean
    }
  }
}

/** In-memory storage (tests, SSR, and environments without `localStorage`). */
export function createMemoryCapsStorage(): AdCapsStorage {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    },
  };
}
